import '../vendors/jquery.swinxy-combined.min';
import '../vendors/jquery.swinxyzoom.min';

// swinxy-combined's pinch/scale (and rotate) gesture recognizers compute the
// distance/midpoint/angle between the two touch points on every "sxy-up"
// (touch-end) event during an active gesture, assuming both points are still
// present - but lifting fingers one at a time (completely normal for a real
// pinch) means the final touch-end fires with fewer than two touches, so
// these read a coordinate off `undefined` and throw, aborting all further
// interaction on the *page* (this is document-level gesture handling, not
// scoped to the zoom widget). This is a bug in the vendored library itself -
// pinch-to-zoom-in (see openOverlay()) is unusable without this guard on all
// three two-point helpers. Patched defensively here rather than editing the
// minified vendor file.
//
// Guarded on swinxytouch actually existing: this runs at import time, and
// shared/lib is imported broadly (every module, plus seraph's own
// ProductZoom) - if a load-order quirk ever left it undefined, an unguarded
// patch here would throw and take down the whole bundle rather than just
// this widget. Also worth knowing: since $.fn.swinxytouch is a shared
// prototype, this patches it for every consumer, seraph's zoom included -
// intentional (the underlying bug isn't webshop-specific) but worth knowing
// if a seraph zoom regression ever traces back here.
type Point = { x: number; y: number };
const swinxytouch = ($.fn as any).swinxytouch;
if (swinxytouch) {
  const guardTwoPointFn = <T>(fn: (a: Point, b: Point) => T, fallback: T) => (a: Point, b: Point) =>
    (a && b) ? fn(a, b) : fallback;
  swinxytouch.d = guardTwoPointFn(swinxytouch.d, 0);
  swinxytouch.a = guardTwoPointFn(swinxytouch.a, 0);
  swinxytouch.m = guardTwoPointFn(swinxytouch.m, { x: 0, y: 0 });
}

export interface ZoomConfig {
  size: string;
  selector: string;
  overlay?: boolean;
}

export class Zoom {

  private zoom: JQuery;
  private container: JQuery;
  private overlay: boolean;
  private scrim: JQuery;
  private closeButton: JQuery;
  private delegationRoot: JQuery;
  private settleTimers: ReturnType<typeof setTimeout>[] = [];
  private patchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(config: ZoomConfig) {
    this.init(config);
  }

  private init(config: ZoomConfig): void {
    const { selector, size, overlay } = config;
    this.overlay = !!overlay;

    // Captured before swinxyzoom touches the DOM: it wraps the selector
    // element in .sxy-zoom-container asynchronously (see buildOverlayChrome
    // below), so right now this is still its plain, stable HAML parent -
    // and stays a stable ancestor of .sxy-zoom-container afterwards too,
    // since wrapping inserts the new container in the same position.
    this.delegationRoot = $(selector).parent();

    this.zoom = ($(selector) as any).swinxyzoom({
      mode: 'dock',
      dock: { position: 'right' },
      size: size,
      controls: false,
      // swinxyzoom lazy-loads the full-resolution zoom image on first open, and
      // once it's loaded jumps straight to a magnified crop by default (a
      // desktop hover-zoom convenience: maxZoom/steps*zoom, which is maxZoom at
      // the library's own 15/15 defaults). For the mobile overlay we want the
      // opposite on open - the whole image, fully zoomed out - so it isn't
      // yanked out from under the fit-to-screen view set up in
      // Zoom#openOverlay; zoom: 0 makes that jump a no-op.
      ...(this.overlay ? { zoom: 0 } : {})
    });

    if (this.overlay) {
      // The overlay CSS (product.scss) keys off this instead of re-deriving
      // ZOOM_OVERLAY_BREAKPOINT itself as a second, independent width check
      // (product_gallery.ts already has the one that decided `overlay` here)
      // - two sources of truth for the same decision drifting apart (a
      // window resized past the breakpoint after this ran, or the ~15px band
      // where a classic scrollbar makes window.innerWidth and @media
      // disagree) is exactly what caused a real bug in an earlier version of
      // this file: the desktop hover-zoom rendering invisible with nothing
      // to restore it, because the CSS believed "mobile" while this instance
      // was never actually built in overlay mode, or vice versa. Keying the
      // CSS off this class instead means it only ever applies to a Zoom
      // instance that genuinely *is* in overlay mode, regardless of the
      // window's width at any later point.
      $('body').addClass('sxy-zoom-overlay-enabled');

      this.setUpPinchZoom();

      // swinxyzoom's dock mode has no overlay/lightbox mode of its own, so the
      // scrim and close button are injected onto <body> (outside
      // .sxy-zoom-container, which CSS repositions as a fixed full-screen panel
      // on mobile) and wired to the same sxy-focus/sxy-blur events the docked
      // viewport already uses to show/hide itself.
      this.buildOverlayChrome();

      // Called after buildOverlayChrome() deliberately: it's what creates
      // delegationRoot's own swinxytouch instance (see this method's own
      // comment), so calling it first means that instance already exists by
      // the time this starts polling for it, rather than relying on that
      // polling to also cover this ordering.
      this.disableTouchPreventDefault();
    }
  }

  private disableTouchPreventDefault(): void {
    // swinxyzoom calls preventDefault() on every touch it sees while
    // tracking a gesture on the container/dragpad, by default - which
    // blocks the page from scrolling at all whenever a scroll happens to
    // start on the photo, regardless of the touch-action CSS on
    // .sxy-zoom-container (real devices don't reliably let touch-action
    // override an explicit preventDefault() the way the spec might suggest).
    // Relying on that same touch-action instead - `pan-y` while closed needs
    // nothing prevented for scrolling to work, and `none` once genuinely
    // open (see product.scss) keeps the browser out of the way of
    // pinch-to-zoom-in without any preventDefault() calls, since zoom()/
    // center() move the image via plain style changes, not by relying on
    // this in any way.
    //
    // This can't be passed in as a swinxyzoom() config option: the gesture
    // recognizer behind that preventDefault call (swinxytouch, a separate
    // jQuery data instance) has its own, entirely separate options object
    // that swinxyzoom's own config is never merged into. It also doesn't
    // exist yet at this point - it's lazily constructed once swinxyzoom
    // finishes preloading the image and wrapping the element into
    // .sxy-zoom-container/.sxy-zoom-dragpad - so this polls until they're
    // available, then patches each directly.
    //
    // delegationRoot needs the same patch: buildOverlayChrome() below binds
    // "sxy-blur" (a swinxytouch special event) on it, delegated, and merely
    // *binding* one - regardless of the delegation selector - lazily
    // constructs a third swinxytouch instance for delegationRoot itself
    // (the same mechanism the container/document.md notes above already
    // ran into once, for "sxy-focus", when it was bound on document instead
    // - see buildOverlayChrome()'s own comment). That instance's touch
    // handling is just as capable of preventing scroll as the container's.
    //
    // ~10s budget, not ~1s: what rt/dp are really waiting on is the
    // product_view image finishing its download (swinxyzoom only builds
    // them inside its own getNaturalSize() load callback), and a budget
    // sized for a fast connection gives up long before that on a slow one -
    // silently leaving the vendor's preventDefault() in place, i.e. the page
    // stays unscrollable from the photo with nothing in the console to say
    // why. Each of the three is patched independently as it appears, rather
    // than gating all three behind whichever is slowest to exist - one that
    // never materialises (unexpected, but cheap to guard) shouldn't leave
    // the other two unpatched.
    //
    // The pending timeout is kept on this.patchTimer, not just a local, so
    // cleanup() can cancel it - at a ~10s budget, a Turbolinks navigation
    // away within that window (landing on a product page, deciding against
    // it, tapping straight back) would otherwise leave this polling for the
    // rest of it against elements ProductGalleryModule.cleanup() has
    // already removed from the DOM.
    let attemptsRemaining = 500;
    const patchTouch = (touch: any): void => {
      if (touch) {
        touch.options.preventDefault = false;
      }
    };
    const tryPatch = (): void => {
      const instance = this.zoom.data('swinxyzoom');
      const rtTouch = instance && instance.rt && instance.rt.j.data('swinxytouch');
      const dpTouch = instance && instance.dp && instance.dp.j.data('swinxytouch');
      const delegationRootTouch = this.delegationRoot.data('swinxytouch');
      patchTouch(rtTouch);
      patchTouch(dpTouch);
      patchTouch(delegationRootTouch);
      if (rtTouch && dpTouch && delegationRootTouch) {
        return;
      }
      if (--attemptsRemaining > 0) {
        this.patchTimer = setTimeout(tryPatch, 20);
      }
    };
    tryPatch();
  }

  private setUpPinchZoom(): void {
    const instance = this.zoom.data('swinxyzoom');
    // instance/_scale are populated synchronously by swinxyzoom() above in
    // every load path this relies on - guarded anyway, since a throw here
    // would abort the constructor before init() gets to bind the thumbnail
    // click handler further down, breaking the gallery entirely rather than
    // just zoom.
    if (!instance || !instance._scale) {
      return;
    }
    let pinchStartLevel = 0;

    // swinxyzoom's own pinch handling computes the new zoom level by
    // multiplying the level the pinch started at by how much further apart
    // the fingers have moved since (level * scale), captured once when the
    // pinch begins. That's fine for the vendor's own desktop hover mode,
    // which starts already zoomed in a meaningful amount - but this overlay
    // deliberately opens fully zoomed out (level 0, to show the whole image
    // undistorted), and multiplying anything by ~0 stays ~0 regardless of
    // how far the fingers spread. In practice that makes pinch-to-zoom-in
    // feel completely dead: even an unrealistically large pinch only reaches
    // a handful of levels out of a maxZoom in the thousands.
    //
    // Replacing the vendor's own scale handlers with our own additive
    // formula - new level = level-at-pinch-start + (howMuchFingersSpread -
    // 1) * maxZoom - fixes that: it's proportional to maxZoom rather than to
    // whatever level the pinch happened to start at, so a comfortable ~2x
    // finger-spread reaches close to full zoom regardless of starting level,
    // while still building on top of (not resetting) wherever a *previous*
    // pinch left off. zoom() clamps the result to [0, maxZoom] itself, so
    // there's no need to do that here too.
    instance._scale[1] = (): void => {
      pinchStartLevel = instance.level;
    };
    instance._scale[2] = (e: { scale: number; position: { x: number; y: number } }): void => {
      instance.zoom(pinchStartLevel + (e.scale - 1) * instance.maxZoom, e.position.x, e.position.y);
    };
  }

  private buildOverlayChrome(): void {
    this.scrim = $('<div class="sxy-zoom-overlay-scrim"></div>').appendTo('body');
    // A <button>, not an <a> with no href: the latter is not in the tab
    // order and doesn't respond to Enter/Space, so a pointer tap on the tiny
    // × (or the scrim) would be the only way out of the overlay for anyone
    // not using touch. aria-label still applies since &times; alone reads as
    // meaningless text to a screen reader.
    this.closeButton = $('<button type="button" class="sxy-zoom-overlay-close" aria-label="Close">&times;</button>').appendTo('body');

    this.scrim.on('click', () => this.closeOverlay());
    this.closeButton.on('click', () => this.closeOverlay());

    // Standard modal behaviour, and the only non-pointer way to dismiss the
    // overlay otherwise. Bound on document (not delegationRoot) since focus
    // can be anywhere once the overlay is open. Namespaced distinctly from
    // delegationRoot's own ".zoom" bindings below (rather than reusing it) -
    // document is a global object shared by the whole page, so a generic
    // namespace there risks colliding with an unrelated ".zoom" binding
    // elsewhere; delegationRoot is this instance's own element, so that risk
    // doesn't apply to its bindings.
    $(document).on('keydown.sxyZoomOverlay', e => {
      if (e.key === 'Escape' && this.container && this.container.hasClass('sxy-zoom-overlay-active')) {
        this.closeOverlay();
      }
    });

    // swinxyzoom's own "sxy-focus" fires the instant a finger touches down -
    // not on a confirmed tap - so opening on it means a scroll gesture that
    // merely starts on the photo pops the overlay open before there's any
    // way to know it wasn't a tap. Tracked directly here from raw
    // touchstart/touchend instead, so opening only happens on a genuine
    // quick, roughly-stationary tap.
    //
    // This deliberately doesn't use swinxyzoom's own "sxy-tap" gesture,
    // which would do the same job: it needs its own host element's gesture-
    // recognizer "focus" state pre-armed to register the first tap of a
    // session correctly (the same fix openOverlay() applies to the dragpad
    // for pinch) - except unlike the dragpad, that state gets blurred again
    // by *any* unrelated touch elsewhere on the page (see the "outside
    // touch" note on closeOverlay()'s cleanup below), so it would need
    // re-arming after every single one, indefinitely, for the life of the
    // page. Plain touch tracking sidesteps that vendor-internal state
    // entirely.
    let touchStart: { x: number; y: number; t: number } | null = null;
    const TAP_MAX_MOVE_PX = 10;
    const TAP_MAX_DURATION_MS = 300;

    this.delegationRoot.on('touchstart.zoom', (e: JQuery.TriggeredEvent) => {
      const touch = (e.originalEvent as TouchEvent).touches[0];
      touchStart = touch ? { x: touch.clientX, y: touch.clientY, t: Date.now() } : null;
    });
    this.delegationRoot.on('touchend.zoom', (e: JQuery.TriggeredEvent) => {
      const start = touchStart;
      touchStart = null;
      if (!start) {
        return;
      }
      const touch = (e.originalEvent as TouchEvent).changedTouches[0];
      const movedPx = touch ? Math.hypot(touch.clientX - start.x, touch.clientY - start.y) : Infinity;
      const elapsedMs = Date.now() - start.t;
      if (movedPx <= TAP_MAX_MOVE_PX && elapsedMs <= TAP_MAX_DURATION_MS) {
        // Suppresses the browser's own synthesized "click" that normally
        // follows a touch tap. That click fires shortly after this
        // touchend, at the same screen coordinate as the tap - but the
        // container is about to become a fixed, fullscreen panel
        // synchronously below, so whatever now happens to sit at that same
        // coordinate in the *new* layout receives it. In practice that's
        // most often the fixed-position close button, which immediately
        // closes the overlay this same tap just opened - it can take
        // several attempts before a tap happens to land somewhere that
        // *doesn't* end up under the close button once the layout changes.
        // Safe to call only here: this branch is reached solely once a
        // touch has already been confirmed as a genuine, stationary tap,
        // never for a scroll/drag (which returns above without preventing
        // anything, so scrolling remains unaffected).
        e.preventDefault();

        // swinxyzoom preloads the image before wrapping the element in
        // .sxy-zoom-container, so it doesn't exist in the DOM at init time -
        // usually long gone by the time a human manages to tap, but not
        // guaranteed on a slow connection (exactly the mobile audience this
        // overlay targets). openOverlay() itself depends on instance.dp/rt,
        // which are built at the same time, so a tap that lands before any
        // of this exists is treated as a no-op rather than half-opening an
        // overlay with no image behind it and no working close path.
        this.container = this.delegationRoot.find('.sxy-zoom-container');
        const instance = this.zoom.data('swinxyzoom');
        if (!this.container.length || !instance || !instance.dp || !instance.rt) {
          return;
        }

        // CSS only makes the container position: fixed once open - it's
        // still a DOM descendant of delegationRoot, so a tap landing
        // *inside* the already-open overlay bubbles up to this same
        // handler. Without this, a stray stationary tap on the zoomed image
        // (a common "tap to dismiss" reflex) would re-run openOverlay() and
        // silently reset the user's pinch back to fully zoomed out.
        if (this.container.hasClass('sxy-zoom-overlay-active')) {
          return;
        }
        this.toggleOverlayChrome(true);
        this.openOverlay();
      }
    });

    // "sxy-blur" IS still swinxyzoom's own event, and still delegated: it's
    // triggered directly on .sxy-zoom-container (both by swinxyzoom's own
    // dock-mode behaviour and by closeOverlay() below), which bubbles up
    // through delegationRoot untouched by the tap handling above.
    this.delegationRoot.on('sxy-blur.zoom', '.sxy-zoom-container', (e: JQuery.TriggeredEvent) => {
      this.container = $(e.currentTarget);
      this.toggleOverlayChrome(false);
      this.container.css({ width: '', height: '' });
    });
  }

  private toggleOverlayChrome(active: boolean): void {
    // Gates the fullscreen container CSS (see product.scss) behind this class so
    // the (invisible until opened) zoom container doesn't sit fixed over the
    // whole page intercepting clicks when the overlay is closed.
    this.container.toggleClass('sxy-zoom-overlay-active', active);
    this.scrim.toggleClass('active', active);
    this.closeButton.toggleClass('active', active);

    // touch-action: none on the container (see product.scss) stops a drag
    // starting on the *photo* from scrolling the page, but a drag starting
    // on the scrim itself has nothing to hand touch-action off to - locking
    // body scroll here covers that gap while the overlay is open.
    $('body').toggleClass('sxy-zoom-overlay-open', active);

    // Without this, a keyboard user would have to tab through the whole
    // page behind the scrim to reach the close button after it opens.
    // Deliberately not a full focus trap / aria-modal (everything behind
    // the scrim stays technically reachable by keyboard) - the audience
    // this overlay targets is touch, so that's judged more machinery than
    // this warrants, but moving focus in and giving it somewhere sane to
    // land back on close is cheap.
    if (active) {
      this.closeButton.trigger('focus');
    } else {
      this.zoom.trigger('focus');
    }
  }

  private openOverlay(): void {
    // swinxyzoom sizes and positions the zoomed image against the container's
    // docked (small) dimensions at load time, and always renders the image at
    // the CONTAINER's aspect ratio rather than the photo's own. Once CSS pops
    // the container fullscreen, both the size and position it already computed
    // are stale, so the image can render off-screen and/or stretched.
    //
    // Resizing the container to a letterboxed box matching the photo's natural
    // aspect ratio *before* rebuilding means swinxyzoom's own maths produce an
    // undistorted, on-screen result; rebuild() re-measures the container to
    // pick that up, and zoom(1, ...)/center(...) below reset to a (near-)fully
    // zoomed-out, centred view of the whole image - a sensible "just opened"
    // state that the user can then pinch in from.
    const instance = this.zoom.data('swinxyzoom');

    // Belt-and-braces alongside the touchend.zoom "already active" guard
    // above, which should already prevent this running twice for one open:
    // if it ever did, this stops the earlier open's pending timers being
    // orphaned rather than cancelled.
    this.clearSettleTimers();

    // The dragpad (where pinch gestures land) has its own independent
    // gesture-recognizer focus state, separate from the container's - it
    // starts unfocused, and the recognizer treats an element's *first* touch
    // while unfocused as a focus transition rather than the start of a
    // gesture. Left alone, that means the very first pinch attempt after
    // every fresh open never has its start distance recorded, so the pinch
    // silently never registers as a scale gesture at all. Pre-focusing it as
    // soon as we open sidesteps that first-touch misclassification.
    //
    // Deferred to the next tick deliberately: focusing also arms a
    // document-level "was that touch outside this widget?" listener, and the
    // very touch event that opened the overlay (the one we're still handling
    // right now) is still bubbling up towards document. Registering that
    // listener synchronously lets it catch that same in-flight event once it
    // arrives there - and since the dragpad has no touches of its own
    // recorded yet, it reads as "touched outside" and immediately blurs
    // itself straight back. Waiting a tick lets the current event finish
    // dispatching first.
    const dragpadTouch = instance.dp.j.data('swinxytouch');
    if (dragpadTouch) {
      setTimeout(() => dragpadTouch.focus(), 0);
    }

    const applyFitZoom = (): void => {
      // A true 0: setUpPinchZoom() above replaces the vendor's own pinch
      // maths with a formula based on maxZoom rather than the level the
      // pinch started at, so - unlike that vendor default - starting
      // exactly here at fully zoomed out doesn't leave pinch-to-zoom-in
      // unable to move anywhere.
      instance.zoom(0, instance.cursor.lastX, instance.cursor.lastY);
      instance.center(instance.si.oW / 2, instance.si.oH / 2, false);
    };

    instance.rebuild(() => {
      // rebuild() is async - it probes the image for its natural size - so
      // this callback can land after the user has already dismissed the
      // overlay (sxy-blur has by then cleared the container's inline size,
      // see the handler in buildOverlayChrome()). Without this guard,
      // re-applying an inline pixel box sized to fill the *screen* below
      // would blow out the layout of what is now a docked, in-page
      // container - and the instance.rebuild(applyFitZoom) call further
      // down would zoom/centre a widget that's no longer open, and create
      // fresh settleTimers after closeOverlay() already cleared them.
      if (!this.container.hasClass('sxy-zoom-overlay-active')) {
        return;
      }

      // instance.si.oW/oH (the photo's real natural dimensions) are only
      // reliable *here*, inside rebuild()'s own callback: rebuild() fetches
      // them itself via a dedicated probe image, independent of whatever
      // load state the live zoomed <img> element happens to be in.
      //
      // Checking that live element's own naturalWidth/naturalHeight instead
      // (as this used to) is unreliable: swinxyzoom lazily loads the full-
      // resolution zoom image on the very first open of a given photo, so at
      // the moment we'd check it, it's often still 0x0 mid-download - and
      // the container, so the whole "fit to screen" view, silently never
      // gets resized at all, leaving the modal open around the photo at its
      // original small size.
      if (instance.si.oW && instance.si.oH) {
        const scale = Math.min(
          window.innerWidth / instance.si.oW,
          window.innerHeight / instance.si.oH
        );
        this.container.css({
          width: `${instance.si.oW * scale}px`,
          height: `${instance.si.oH * scale}px`
        });

        // A single tap fires sxy-focus twice (swinxyzoom's own gesture
        // handling, not something we control). The plugin's own _focus()
        // compares the container's current size against the size it saw
        // last time, and - as a convenience for e.g. a responsive dock panel
        // resizing between separate hovers - auto-corrects by force-zooming
        // to maxZoom if they differ. Recording the resized dimensions here,
        // synchronously and immediately, satisfies that check so it has
        // nothing left to "fix" regardless of when that second sxy-focus
        // lands. Doing this after the rebuild below instead (i.e. once
        // applyFitZoom() itself runs) is too late: that rebuild is its own
        // separate async round trip, and the second sxy-focus firing inside
        // that gap would still see a stale, pre-resize `last` and stomp
        // straight back to maxZoom before applyFitZoom() gets a chance to
        // run at all.
        instance.last = { w: instance.rt.j.width(), h: instance.rt.j.height() };

        // The container's size just changed, so dp/vp - measured against the
        // old size a moment ago - are now stale; rebuild again to pick up
        // the new size before applying the fitted zoom/centre.
        instance.rebuild(applyFitZoom);
      } else {
        applyFitZoom();
      }

      // swinxyzoom also lazy-loads the full-resolution zoom image on the
      // very first open of a session, and once that (separately
      // asynchronous) load settles, unconditionally re-applies a zoom level
      // derived from the zoom/steps options passed to swinxyzoom() above -
      // 0 given the zoom: 0 option set above, so this normally lands
      // harmlessly on the same fitted view already applied. It's only worth
      // guarding against at all in case that settling happens to land after
      // the *first* pinch of a session has already moved the level
      // elsewhere - reapplying the fitted view at that point would yank it
      // back out from under the user's fingers. So this is reapplied a few
      // times shortly after opening, but only while the level still reads
      // exactly 0 (never overriding a real pinch in progress), and every
      // pending check is cancelled outright the moment the user actually
      // touches the dragpad - once real interaction has started, this
      // safety net has nothing useful left to do.
      // Stored on the instance (rather than a local, as before) so
      // closeOverlay()/cleanup() can clear them - closing mid-window
      // otherwise left up to three pending callbacks holding a reference to
      // `instance` for nothing. `.off('.zoomOpenGuard')` first because
      // `.one()` with a space-separated list removes each type only once
      // *that* type fires - on a touch device the mousedown half never
      // fires, so without this every open leaked one dead handler on the
      // dragpad.
      this.settleTimers = [50, 150, 400].map(delay => setTimeout(() => {
        if (this.container.hasClass('sxy-zoom-overlay-active') && instance.level === 0) {
          applyFitZoom();
        }
      }, delay));
      instance.dp.j.off('.zoomOpenGuard').one('touchstart.zoomOpenGuard mousedown.zoomOpenGuard', () => {
        this.clearSettleTimers();
      });
    });
  }

  private clearSettleTimers(): void {
    this.settleTimers.forEach(clearTimeout);
    this.settleTimers = [];
  }

  private closeOverlay(): void {
    // Closing mid-window (see openOverlay()'s settleTimers comment) would
    // otherwise leave up to three pending callbacks alive.
    this.clearSettleTimers();

    // The container's gesture recognizer (swinxytouch, a separate jQuery data
    // instance from the swinxyzoom one) tracks its own "focus" state and, while
    // focused, keeps a document-level touchstart listener bound so it can
    // detect an outside touch. That's only ever released via the recognizer's
    // own blur() - triggering the "sxy-blur" *event* alone (which is all the
    // rest of this class needs, to reuse swinxyzoom's own dock hide/collapse
    // behaviour and to fire our own sxy-blur handler above) does not call it.
    // Skipping this leaves that listener permanently attached, so every
    // subsequent click on the page - not just this widget - gets fed through
    // gesture handling that no longer has a matching touch to pair it with,
    // eventually throwing inside swinxyzoom's own handlers.
    const touch = this.container.data('swinxytouch');
    if (touch) {
      touch.blur();
    }

    // The dragpad has this same kind of gesture-recognizer state, independent
    // of the container's - pre-focused in openOverlay() so the first pinch
    // of a session is recognised correctly. Mirror that cleanup here so it
    // doesn't leak its own document listener the same way.
    const instance = this.zoom.data('swinxyzoom');
    const dragpadTouch = instance && instance.dp && instance.dp.j.data('swinxytouch');
    if (dragpadTouch) {
      dragpadTouch.blur();
    }

    // openOverlay() records the resized (fullscreen) container dimensions
    // into swinxyzoom's own `last` so its own _focus() doesn't mistake our
    // resize for something it needs to self-heal (see that comment for the
    // full explanation). Closing shrinks the container straight back down
    // to its normal docked size without going through openOverlay() again,
    // so if `last` is left holding the fullscreen size, the very first
    // _focus() of the *next* open sees a real, correct mismatch against the
    // now-small container - the exact same size-changed check, just now
    // legitimately true - and force-zooms to maxZoom before openOverlay()
    // ever gets a chance to run. Resetting it back to swinxyzoom's own
    // initial value here means every open starts that check fresh, exactly
    // as if the container had never been resized before at all.
    if (instance) {
      instance.last = false;
    }

    this.container.trigger('sxy-blur');
  }

  public cleanup(): void {
    if (!this.overlay) {
      return;
    }
    // Turbolinks can navigate away with the overlay still open, which never
    // goes through closeOverlay() otherwise - leaving both gesture
    // recognizers' document-level touchstart listeners bound to elements
    // that are about to be detached below. closeOverlay() explains why that
    // eventually throws on every subsequent click on the page, not just
    // this widget's.
    if (this.container && this.container.hasClass('sxy-zoom-overlay-active')) {
      this.closeOverlay();
    }
    this.clearSettleTimers();
    // Cancels disableTouchPreventDefault()'s polling if it's still waiting
    // on the vendor's lazily-built rt/dp (see that method's own comment on
    // why its budget is long enough to still be running at this point).
    if (this.patchTimer) {
      clearTimeout(this.patchTimer);
    }
    this.delegationRoot.off('.zoom');
    // Escape handling is bound on document, not delegationRoot, so it needs
    // its own unbind here.
    $(document).off('.sxyZoomOverlay');
    // Turbolinks re-navigation detaches the underlying element without ever
    // going through closeOverlay(), which would otherwise unbind this via
    // the settleTimers cleanup above - left bound to a now-detached element
    // otherwise.
    const instance = this.zoom.data('swinxyzoom');
    if (instance && instance.dp) {
      instance.dp.j.off('.zoomOpenGuard');
    }
    $('body').removeClass('sxy-zoom-overlay-open');
    $('body').removeClass('sxy-zoom-overlay-enabled');
    if (this.scrim) {
      this.scrim.remove();
    }
    if (this.closeButton) {
      this.closeButton.remove();
    }
  }

}